/**
 * EG Search (Meilisearch) – autocomplete renderer.
 *
 * Pure rendering helpers for the dropdown (no event handling).
 */
window.EGSearchAutocompleteRender = (function () {
  'use strict';

  const config = window.EGSearchConfig;

  // Quick access cards (right side)
  function renderQuickAccessCard(item) {
    if (item.style === 'help') {
      const links = Array.isArray(item.links)
        ? item.links.filter((link) => link?.title && link?.url)
        : [];
      return `
        <div class="aa-Item">
          <div class="aa-QuickAccessItem aa-QuickAccessItem--help">
            <div class="aa-ItemContent">
              <div class="aa-ItemContentBody">
                <h3 class="aa-ItemContentTitle">${config.escapeHtml(item.title || 'How Can We Help?')}</h3>
                <ul>
                  ${links.map(link => `<li><a href="${config.escapeHtml(link.url)}">${config.escapeHtml(link.title)}</a></li>`).join('')}
                </ul>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    const cardStyle = item.style === 'sales-banner' ? 'sales-banner' : 'new-collection';
    return `
      <div class="aa-Item">
        <a href="${config.escapeHtml(item.url || '#')}" class="aa-QuickAccessItem aa-QuickAccessItem--${cardStyle}">
          <div class="aa-ItemPicture">
            <img src="${config.escapeHtml(item.image || '')}" alt="${config.escapeHtml(item.title || '')}" loading="lazy" />
          </div>
          <div class="aa-ItemContent">
            <div class="aa-ItemContentBody">
              <h3 class="aa-ItemContentTitle">${config.escapeHtml(item.title || '')}</h3>
            </div>
          </div>
        </a>
      </div>
    `;
  }

  // Default panel (empty query)
  function renderDefaultPanel(container) {
    container.innerHTML = `
      <div class="aa-Panel--scrollable">
        <div class="aa-PanelSections page-width">
          <!-- Popular searches -->
          <div class="aa-PanelSection aa-PanelSection--left aa-PanelSection--popular">
            <div class="aa-SourceHeader">
              <span class="aa-SourceHeaderTitle">Popular Searches</span>
            </div>
            <div class="aa-List">
              ${config.POPULAR_SEARCHES.map(term => `
                <div class="aa-Item" role="option">
                  <a href="/search?q=${encodeURIComponent(term)}" class="aa-ItemLink">
                    <div class="aa-ItemWrapper">
                      <span class="aa-ItemContentTitle">${term}</span>
                    </div>
                  </a>
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Quick access -->
          <div class="aa-PanelSection aa-PanelSection--right aa-PanelSection--quickAccess">
            <div class="aa-SourceHeader">
              <span class="aa-SourceHeaderTitle">Quick Access</span>
            </div>
            <div class="aa-List">
              ${config.QUICK_ACCESS.map(item => renderQuickAccessCard(item)).join('')}
            </div>
          </div>
        </div>
      </div>
    `;
    // Visibility is controlled via the `.open` class in CSS.
  }

  // Article card (blogs)
  function renderArticleCard(article) {
    let dateStr = '';
    if (article.date) {
      try {
        const date = new Date(article.date);
        dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {
        dateStr = '';
      }
    }

    return `
      <div class="aa-Item" role="option">
        <a href="${article.url || '/blogs/epic-gardening/' + article.slug}" class="aa-ArticleLink aa-ItemLink">
          ${article.image_url ? `
          <div class="aa-ArticlePicture">
            <img src="${article.image_url}" alt="${config.escapeHtml(article.title)}" loading="lazy" />
          </div>
          ` : ''}
          <div class="aa-ArticleContent">
            <div class="aa-ArticleTitle">${article._formatted?.title || config.escapeHtml(article.title)}</div>
            ${dateStr ? `<div class="aa-ArticleDate">${dateStr}</div>` : ''}
          </div>
        </a>
      </div>
    `;
  }

  // Product card
  function renderProductCard(product, index = 0, query = '') {
    const hasDiscount = product.compare_at_price > 0 && product.compare_at_price > product.price;

    return `
      <div class="aa-Item" role="option">
        <a href="/products/${product.handle}"
           class="aa-ProductItem aa-ItemLink"
           data-eg-track-click="1"
           data-eg-query="${config.escapeHtml(query)}"
           data-eg-search-id="${config.escapeHtml(product.__search_id || '')}"
           data-eg-product-id="${config.escapeHtml(String(product.id || product.objectID || ''))}"
           data-eg-product-title="${config.escapeHtml(product.title || '')}"
           data-eg-position="${index + 1}"
           data-eg-index-name="products"
           data-eg-search-type="autocomplete">
          <div class="aa-ItemContent">
            <div class="aa-ItemPicture aa-ItemPicture--loaded">
              <img src="${product.image_url || '/no-image.png'}" alt="${config.escapeHtml(product.title)}" loading="lazy" />
            </div>
            <div class="aa-ItemContentBody">
              ${product.vendor ? `<div class="aa-ItemContentBrand">${config.escapeHtml(product.vendor)}</div>` : ''}
              <div class="aa-ItemContentTitle">${product._formatted?.title || config.escapeHtml(product.title)}</div>
              <div class="aa-ItemContentPrice">
                <span class="aa-ItemContentPriceCurrent">$${product.price}</span>
                ${hasDiscount ? `<span class="aa-ItemContentPriceDiscounted">$${product.compare_at_price}</span>` : ''}
              </div>
            </div>
          </div>
        </a>
      </div>
    `;
  }

  // Suggestions shown on the left
  function generateSuggestions(query, products) {
    let suggestionItems = [];
    const seenTerms = new Set();
    const queryLower = query.toLowerCase();

    const skipPatterns = [
      /^subcat/i, /^subcategory/i, /^category/i, /^cat[-_]/i,
      /^meta/i, /^tag[-_]/i, /^collection/i, /^type[-_]/i,
      /^\d+$/, /^[a-z]{1,2}$/i
    ];

    function shouldSkip(term) {
      return skipPatterns.some(pattern => pattern.test(term.trim()));
    }

    function addSuggestion(term, priority = 0) {
      const cleanTerm = term.trim().replace(/[-_:]/g, ' ').replace(/\s+/g, ' ');
      const termLower = cleanTerm.toLowerCase();

      if (termLower.length < 2 || seenTerms.has(termLower) || !termLower.includes(queryLower) || shouldSkip(cleanTerm)) {
        return;
      }

      seenTerms.add(termLower);
      suggestionItems.push({ title: cleanTerm, searchTerm: termLower, priority });
    }

    addSuggestion(query, 100);

    if (products && products.length) {
      const productTypeCounts = {};
      const vendorCounts = {};

      products.forEach(p => {
        if (p.product_type) {
          productTypeCounts[p.product_type] = (productTypeCounts[p.product_type] || 0) + 1;
        }
        if (p.vendor) {
          vendorCounts[p.vendor] = (vendorCounts[p.vendor] || 0) + 1;
        }
      });

      Object.entries(productTypeCounts)
        .sort((a, b) => b[1] - a[1])
        .forEach(([type, count]) => {
          if (type.toLowerCase().includes(queryLower)) {
            addSuggestion(type, 90);
          }
        });

      Object.entries(vendorCounts)
        .sort((a, b) => b[1] - a[1])
        .forEach(([vendor, count]) => {
          if (vendor.toLowerCase().includes(queryLower)) {
            addSuggestion(vendor, 60);
          }
        });

      products.slice(0, 15).forEach(p => {
        const title = p.title;
        const words = title.split(/\s+/);

        const queryWordIndex = words.findIndex(w => w.toLowerCase().includes(queryLower));
        if (queryWordIndex >= 0) {
          const start = Math.max(0, queryWordIndex - 1);
          const end = Math.min(words.length, queryWordIndex + 2);
          const phrase = words.slice(start, end).join(' ');
          if (phrase.length > query.length + 2) {
            addSuggestion(phrase, 50);
          }
        }
      });
    }

    suggestionItems.sort((a, b) => b.priority - a.priority);
    return suggestionItems.slice(0, window.matchMedia('(max-width: 989px)').matches ? 3 : 8);
  }

  // Populated panel (query results)
  function renderSearchPanel(query, suggestions, products, totalProducts, articles, totalArticles, container) {
    const suggestionItems = generateSuggestions(query, products);

    // De-dupe products (the index can return multiple docs per product).
    const uniqueProducts = [];
    const seenProductHandles = new Set();
    if (products && products.length) {
      products.forEach(p => {
        if (p.handle && !seenProductHandles.has(p.handle)) {
          seenProductHandles.add(p.handle);
          uniqueProducts.push(p);
        }
      });
    }

    const hasArticles = articles && articles.length > 0;

    const editorial = !!document.querySelector('.eg-shared-header');
    const scopes = `<nav class="eg-search-scopes" aria-label="Search in"><a href="/search?q=${encodeURIComponent(query)}" ${editorial ? '' : 'aria-current="page"'}>Products</a><a href="/?s=${encodeURIComponent(query)}" ${editorial ? 'aria-current="page"' : ''}>Growing guides</a></nav>`;
    if (!uniqueProducts.length && !hasArticles) {
      container.innerHTML = `${scopes}<div class="eg-search-empty" role="status"><h2>No results for &ldquo;${config.escapeHtml(query)}&rdquo;</h2><p>Try a shorter search or check the spelling.</p><a href="/collections/all">Browse products</a><a href="/learn/">Explore growing guides</a></div>`;
      return;
    }
    container.innerHTML = `
      ${scopes}
      <div class="aa-Panel--scrollable">
        <div class="aa-PanelSections page-width">
          <!-- Left: Suggestions -->
          <div class="aa-PanelSection aa-PanelSection--left">
            <div class="aa-SourceHeader">
              <span class="aa-SourceHeaderTitle">Suggestions</span>
            </div>
            <div class="aa-PanelSectionSources">
              <div class="aa-List">
                ${suggestionItems.slice(0, 10).map((item, index) => `
                  <div class="aa-Item" role="option" aria-selected="${index === 0 ? 'true' : 'false'}">
                    <a href="/search?q=${encodeURIComponent(item.title || item)}" class="aa-ItemLink aa-ItemWrapper">
                      <div class="aa-ItemIcon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                          <circle cx="11" cy="11" r="8"></circle>
                          <path d="m21 21-4.35-4.35"></path>
                        </svg>
                      </div>
                      <div class="aa-ItemContent">
                        <span class="aa-ItemContentTitle">${config.highlightMatch(item.title || item, query)}</span>
                      </div>
                    </a>
                  </div>
                `).join('')}
              </div>
            </div>
          </div>

          <!-- Right: Products + Articles -->
          <div class="aa-PanelSection aa-PanelSection--right">
            <!-- Products Section -->
            <div class="aa-PanelSection aa-PanelSection--products" data-autocomplete-source-id="productsPlugin" style="${uniqueProducts.length ? '' : 'display:none;'}">
              <div class="aa-SourceHeader">
                <span class="aa-SourceHeaderTitle">Products For ${config.escapeHtml(query)}</span>
              </div>
              <div class="aa-List">
                ${uniqueProducts.slice(0, 4).map((product, idx) => renderProductCard(product, idx, query)).join('')}
              </div>
              <div class="aa-SourceFooter">
                <div>
                  <a href="/search?q=${encodeURIComponent(query)}" class="aa-SeeAllBtn">
                    See all products
                  </a>
                </div>
              </div>
            </div>

            <!-- Articles Section -->
            <div class="aa-PanelSection aa-PanelSection--articles" data-autocomplete-source-id="articlesPlugin" style="${hasArticles ? '' : 'display: none;'}">
              <div class="aa-SourceHeader">
                <span class="aa-SourceHeaderTitle">Articles For ${config.escapeHtml(query)}</span>
              </div>
              <div class="aa-List aa-List--articles">
                ${hasArticles ? articles.slice(0, 3).map(article => renderArticleCard(article)).join('') : ''}
              </div>
              ${hasArticles ? `
              <div class="aa-SourceFooter">
                <div>
                  <a href="https://www.epicgardening.com/?s=${encodeURIComponent(query)}" class="aa-SeeAllBtn">
                    See all growing guides
                  </a>
                </div>
              </div>
              ` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
    // Visibility is controlled via the `.open` class in CSS.

    // Hover should move the keyboard "active" row, too.
    container.querySelectorAll('.aa-PanelSection--left .aa-Item').forEach(item => {
      item.addEventListener('mouseenter', function () {
        container.querySelectorAll('.aa-PanelSection--left .aa-Item').forEach(i => i.setAttribute('aria-selected', 'false'));
        this.setAttribute('aria-selected', 'true');
      });
    });
  }

  return {
    renderDefaultPanel,
    renderSearchPanel,
    renderProductCard,
    renderArticleCard,
    renderQuickAccessCard,
    generateSuggestions
  };
})();
